import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LayoutDashboard, Route, Activity, Users, Settings, Pencil, GitBranch, PlayCircle } from "lucide-react";
import { useHelpTour } from "@/hooks/useHelpTour";
import { useAuth } from "@/hooks/useAuth";

const iconMap: Record<string, React.ElementType> = {
  LayoutDashboard,
  Route,
  Activity,
  Users,
  Settings,
  Pencil,
  GitBranch,
};

export default function Help() {
  const { startTour, getTours } = useHelpTour();
  const { isSuperAdmin } = useAuth();
  const tours = getTours();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Aide</h1>
        <p className="mt-1 text-muted-foreground">
          Explorez les guides interactifs pour comprendre le fonctionnement de l'administration. L'aide est également
          disponible sur chaque page concernée, en cliquant sur l'icone aide ? en haut de la page.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tours.map((tour) => {
          const Icon = iconMap[tour.icon] || Activity;
          return (
            <Card key={tour.key} className="border-border/50 hover:border-primary/30 transition-colors">
              <CardHeader className="flex flex-row items-start gap-3 pb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">{tour.title}</CardTitle>
                    {tour.superAdminOnly && (
                      <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-600">
                        Super Admin
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-muted-foreground mb-4">{tour.description}</p>
                <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => startTour(tour.key)}>
                  <PlayCircle className="h-4 w-4" />
                  Lancer le guide
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-border/50 bg-muted/30">
        <CardContent className="py-6">
          <h3 className="font-semibold mb-2">💡 Comment utiliser les guides ?</h3>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            <li>
              • Cliquez sur <strong>« Lancer le guide »</strong> pour démarrer un tour interactif.
            </li>
            <li>
              • Le guide vous redirigera automatiquement vers la page concernée et mettra en surbrillance les éléments
              importants.
            </li>
            <li>
              • Naviguez entre les étapes avec les boutons <strong>Suivant</strong> / <strong>Précédent</strong>.
            </li>
            <li>• Vous pouvez quitter le guide à tout moment en cliquant en dehors ou sur le bouton de fermeture.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
